import { accessToken, refreshAccess } from "./auth";

const CLIENT_CORRELATION_KEY = "kanban.client-correlation-id";
let fallbackClientCorrelationId: string | null = null;

function randomId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Each browser tab gets its own durable correlation id. Mutations carry this
// through the API into the event ledger, which lets the tab discard its own SSE
// echo without hiding changes made by another tab signed in as the same user.
export function clientCorrelationId(): string {
  if (fallbackClientCorrelationId) return fallbackClientCorrelationId;
  try {
    const stored = globalThis.sessionStorage?.getItem(CLIENT_CORRELATION_KEY);
    if (stored) return (fallbackClientCorrelationId = stored);
    const created = randomId();
    globalThis.sessionStorage?.setItem(CLIENT_CORRELATION_KEY, created);
    return (fallbackClientCorrelationId = created);
  } catch {
    return (fallbackClientCorrelationId = randomId());
  }
}

export interface ResponseEnvelope<T> {
  body: T;
  etag: string | null;
  status: number;
}

export class ApiProblem extends Error {
  status: number;
  problem: Record<string, unknown>;

  constructor(status: number, problem: Record<string, unknown>) {
    super(String(problem.detail || problem.title || `Request failed (${status})`));
    this.name = "ApiProblem";
    this.status = status;
    this.problem = problem;
  }
}

export function newIdempotencyKey(): string {
  return randomId();
}

export async function authorizedFetch(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = accessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("X-Correlation-ID")) {
    headers.set("X-Correlation-ID", clientCorrelationId());
  }
  const response = await fetch(path, { ...init, headers });
  if (response.status === 401 && !retried && (await refreshAccess())) {
    return authorizedFetch(path, init, true);
  }
  if (response.status === 401 && !retried) {
    window.dispatchEvent(new CustomEvent("kanban:authentication-required"));
  }
  return response;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: { etag?: string | number; idempotencyKey?: string } = {},
): Promise<ResponseEnvelope<T>> {
  const headers = new Headers({ Accept: "application/json" });
  if (body !== undefined) headers.set("Content-Type", "application/json");
  if (options.etag !== undefined) headers.set("If-Match", String(options.etag));
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
  const response = await authorizedFetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    let problem: Record<string, unknown> = {};
    try {
      problem = (await response.json()) as Record<string, unknown>;
    } catch {
      problem = { detail: response.statusText };
    }
    throw new ApiProblem(response.status, problem);
  }
  const value = response.status === 204 ? undefined : await response.json();
  return {
    body: value as T,
    etag: response.headers.get("ETag"),
    status: response.status,
  };
}
